import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert, Button } from '@mantine/core';
import { useMsal } from '@azure/msal-react';
import { AuthProvider } from './components/AuthProvider';
import { AuthRefreshHandler } from './components/AuthRefreshHandler';
import { Dashboard } from './components/dashboard/Dashboard';
import { AppShell } from './components/layout';
import { PersonManagement } from './components/users';
import { LocalityManagement, LocalityEntityManagement } from './components/localities';
import DynamicEntityManagement from './components/entities/DynamicEntityManagement';
import SchemaBindingManagement from './components/schemas/SchemaBindingManagement';
import { GroupsManagement } from './components/groups';
import { SchoolMapView } from './components/map';
import { ServiceProviderConfigManagement } from './components/serviceProvider';
import { ScimApiTesting, JsTesting } from './components/testing';
import { ScimFilterConverterUI } from './components/common/ScimFilterConverter';
import { PatchTesting } from './components/patchTesting';
import ScimSelect from './components/scimSelect/ScimSelect';
import SpeedComparison from './components/utilities/SpeedComparison';
import SetGen from './components/utilities/SetGen';
import ContentTypesTesting from './components/utilities/ContentTypesTesting';
import SearchIndexes from './components/utilities/SearchIndexes';
import SearchCompare from './components/utilities/SearchCompare';
import UserSettingsTable from './components/utilities/UserSettingsTable';
import FreeEdit from './components/utilities/FreeEdit';
import EncDec from './components/utilities/EncDec';

import PolicyRuleAudit from './components/utilities/PolicyRuleAudit';
import { FunctionCatalogManagement } from './components/admin/FunctionCatalogManagement';
import { PluginFunctionsManagement } from './components/admin/PluginFunctionsManagement';
import { Footer } from './components/Footer';

import { LandingPage } from './components/LandingPage';
import { EntityType, getEntityTypeString } from './types/ScimUser';
import { scimClassService } from './services/ScimLocalityService';
import type { Class } from './types/Localities';
import { DisplayNameCell, type TableColumn } from './components/common';
import { entityService } from './services/entityServiceSingleton';
import { parseScimReferenceUrl } from './utils/scimUrlUtils';
import { getSingularLabel } from './utils/pluralizationUtils';
import SchemaCacheService, { FieldOption } from './services/SchemaCacheService';
import { ScimSchemaService } from './services/ScimSchemaService';
import { EntityTypesProvider } from './contexts/EntityTypesContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ViewModeProvider } from './contexts/ViewModeContext';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { mantineTheme } from './theme/mantineTheme';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { EntityCategorizationService } from './services/EntityCategorizationService';
import { initializeEntityActions } from './services/EntityActionInitializer';
import EntityMetadataService from './services/EntityMetadataService';
import MeService from './services/MeService';
import EntitlementsService from './services/EntitlementsService';
import { useEntityEntitlements } from './hooks/useEntityEntitlements';
import { useFieldOptions, useSchemaLoading, useSchemaError } from './store/schemaHooks';
import { getSchemaEntityIdForDestination } from './utils/schemaEntityIdUtils';
import { debugVerboseLog } from './utils/debugLog';
import { getEntityColor } from './utils/navigationIconUtils';
import type { EntityScreenMode } from './utils/entityScreenRouteUtils';
import { parseEntityScreenHash } from './utils/entityScreenRouteUtils';
import { ShadcnUnsavedChangesDialog } from './components/dialogs/ShadcnUnsavedChangesDialog';
import {
    type AppRoute,
    DASHBOARD_ROUTE,
    routeToDestination,
    routeToEntityType,
    routeToEntityId,
    routeToViewMode,
    routeToCategory,
} from './routes/types';
import { parseHash as parseHashRoute, parseHashAsync } from './routes/hashRouter';

export interface RecentPage {
    destination: string;
    hash: string;
    label: string;
    icon: string;
    color: string;
    timestamp: number;
    /** Category prefix (admin, other, meta, localities, person) for path rebuilding. */
    category?: 'admin' | 'other' | 'meta' | 'localities' | 'person';
    /** Path segments after the category (entity type first, then subpath e.g. ['pluginfunctions', 'invoke']). Used to build tooltip with singularized entity + reattached subpath. */
    pathAfterCategory?: string[];
}

const MAX_RECENT_PAGES = 5;

type NavigationCategory = 'person' | 'locality' | 'other' | 'meta' | 'utilities' | 'blacklist' | 'admin';

type PendingNavigation =
    | {
        kind: 'hash';
        hash: string;
    }
    | {
        kind: 'destination';
        destination: string;
        isEntity: boolean;
        category?: NavigationCategory;
    };

interface InlineEditorUnsavedState {
    active: boolean;
    hasUnsavedChanges: boolean;
    mode: 'create' | 'edit';
    entityType: string;
}

const EMPTY_INLINE_EDITOR_UNSAVED_STATE: InlineEditorUnsavedState = {
    active: false,
    hasUnsavedChanges: false,
    mode: 'edit',
    entityType: 'entity'
};

// Separate component that uses useMsal hook (must be inside AuthProvider)
function AppContent() {
    const { accounts, inProgress } = useMsal();
    // Import DirectTokenService dynamically to avoid circular dependencies
    const [isDirectTokenAuth, setIsDirectTokenAuth] = React.useState(false);

    React.useEffect(() => {
        // Check for direct token authentication
        const checkDirectToken = async () => {
            const { DirectTokenService } = await import('./services/DirectTokenService');
            setIsDirectTokenAuth(DirectTokenService.hasDirectToken());
        };
        checkDirectToken();
    }, []);

    const isAuthenticated = accounts.length > 0 || isDirectTokenAuth;

    // Fetch Me endpoint data on app startup if authenticated
    React.useEffect(() => {
        if (isAuthenticated) {
            // Only fetch if not already cached and not currently loading
            if (MeService.getMe() === null && !MeService.isLoading()) {
                MeService.fetchMe().catch((error) => {
                    // Silently fail - Me data will be fetched when needed
                    console.debug('Failed to fetch Me data on startup:', error);
                });
            }
        }
    }, [isAuthenticated]);

    // Cache entity entitlements (mgmt/Entitlements/entity) for nav visibility and table refresh when on-behalf-of changes
    const { entityEntitlements } = useEntityEntitlements();
    /** Changes when on-behalf-of is toggled or UPN changes (requestor identity from entitlements); used to refresh tables. */
    const onBehalfOfKey = entityEntitlements?.Requestor
        ? `${entityEntitlements.Requestor.Id}:${entityEntitlements.Requestor.EntityType}`
        : '';
    const requestApprovalSummary = React.useMemo(
        () => entityEntitlements?.RequestApprovalTypeSummary ?? [],
        [entityEntitlements]
    );
    React.useEffect(() => {
        if (!isAuthenticated) return;
        EntitlementsService.fetchEntityEntitlements().catch(() => {
            console.debug('Failed to fetch entity entitlements on startup');
        });
    }, [isAuthenticated]);

    // Handle middle mouse button clicks on links to open in new window
    React.useEffect(() => {
        const handleMiddleClick = (e: MouseEvent) => {
            // Check if middle mouse button (button 1) was clicked
            if (e.button === 1) {
                const target = e.target as HTMLElement;

                // Find the closest link element with href
                let linkElement: HTMLElement | null = target.closest('a[href]');

                if (linkElement) {
                    const href = linkElement.getAttribute('href');
                    if (href && !href.startsWith('javascript:') && !href.startsWith('#')) {
                        // Prevent default behavior
                        e.preventDefault();
                        e.stopPropagation();

                        // Open in new window
                        window.open(href, '_blank');
                    }
                }
            }
        };

        // Add event listener for mousedown (fires before click)
        // Use capture phase to catch events before they're handled by React
        document.addEventListener('mousedown', handleMiddleClick, true);

        return () => {
            document.removeEventListener('mousedown', handleMiddleClick, true);
        };
    }, []);

    const [navOpen, setNavOpen] = useState(true);
    const [navRailOpen, setNavRailOpen] = useState(true);
    // --- Consolidated route state (Phase 3 refactor) ---
    const [route, setRoute] = useState<AppRoute>(DASHBOARD_ROUTE);
    const [recentPages, setRecentPages] = useState<RecentPage[]>([]);
    const [inlineEditorUnsavedState, setInlineEditorUnsavedState] = useState<InlineEditorUnsavedState>(EMPTY_INLINE_EDITOR_UNSAVED_STATE);
    const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
    const [unsavedNavigationDialogOpen, setUnsavedNavigationDialogOpen] = useState(false);

    // Derived route values (backwards-compat with existing code)
    const selectedDestination = routeToDestination(route);
    const selectedEntityType = routeToEntityType(route) as EntityType | undefined;
    const selectedEntityId = routeToEntityId(route);
    const selectedEntityViewMode: EntityScreenMode = routeToViewMode(route);
    const entityCategory = routeToCategory(route);

    // Plugin route fields (only meaningful when route.type === 'plugin')
    const selectedPluginFunctionId = route.type === 'plugin' ? route.pluginId ?? null : null;
    const selectedPivotEntityId = route.type === 'plugin' ? route.pivotEntityId ?? null : null;
    const selectedPivotDetailId = route.type === 'plugin' ? route.pivotDetailId ?? null : null;
    const selectedPrepareCatalogId = route.type === 'plugin' ? route.prepareCatalogId ?? null : null;
    const selectedPrepareDetailId = route.type === 'plugin' ? route.prepareDetailId ?? null : null;

    const activePageKey = selectedEntityType && selectedDestination === 'Person'
        ? String(selectedEntityType)
        : selectedDestination;

    const dismissRecentPage = useCallback((destination: string) => {
        setRecentPages(prev => {
            const remaining = prev.filter(p => p.destination !== destination);
            if (destination === activePageKey) {
                if (remaining.length > 0) {
                    window.location.hash = remaining[0].hash;
                } else {
                    window.location.hash = 'dashboard';
                }
            }
            return remaining;
        });
    }, [activePageKey]);

    // Authentication state tracking
    const [authResolved, setAuthResolved] = useState(false);

    // Table sorting is currently enabled by default.

    const handleInlineEditorUnsavedStateChange = useCallback((state: InlineEditorUnsavedState) => {
        setInlineEditorUnsavedState(state.active ? state : EMPTY_INLINE_EDITOR_UNSAVED_STATE);
    }, []);

    // Table hash query params (search/filter/page/etc.) are interpreted by table components, not App.

    // Schema data from Redux
    const fieldOptions = useFieldOptions();
    const schemaLoading = useSchemaLoading();
    const schemaError = useSchemaError();

    // Per-entity (tier2) entitlements are fetched by the table hook (usePerEntityTableEntitlement) when the table mounts; no prefetch here to avoid duplicate calls.

    // Helper function to format entity type for display
    const formatEntityTypeForDisplay = (entityType?: EntityType): string => {
        if (!entityType) return '';
        // Convert to title case (first letter uppercase, rest lowercase)
        const entityTypeString = getEntityTypeString(entityType);
        return entityTypeString.charAt(0).toUpperCase() + entityTypeString.slice(1).toLowerCase();
    };

    // Get display label for destination
    // Special cases that don't use pluralizer or don't get " Management" suffix
    const getDestinationLabel = (): string => {
        if (selectedDestination === 'dashboard') {
            return 'Dashboard';
        }
        if (selectedDestination === 'Person') {
            return selectedEntityType ? `${getSingularLabel(getEntityTypeString(selectedEntityType))} Management` : 'All Users';
        }

        // Utility/testing pages that don't get " Management"
        if (selectedDestination === 'schoolMap') {
            return 'School Map';
        }
        if (selectedDestination === 'testing') {
            return 'SCIM API Testing';
        }
        if (selectedDestination === 'jsTesting') {
            return 'JavaScript Utilities Testing';
        }
        if (selectedDestination === 'customFilter') {
            return 'Custom Filter Builder';
        }
        if (selectedDestination === 'patchTesting') {
            return 'Patch Testing';
        }
        if (selectedDestination === 'scimSelect') {
            return 'SCIM Select Tester';
        }
        if (selectedDestination === 'speedComparison') {
            return 'Speed Comparison Utility';
        }
        if (selectedDestination === 'encdec') {
            return 'EncDec';
        }
        if (selectedDestination === 'setGen') {
            return 'Set & Group Generator Utility';
        }
        if (selectedDestination === 'policyRuleAudit') {
            return 'Policy Rule Audit';
        }
        if (selectedDestination === 'contentTypes') {
            return 'Content Types Testing';
        }
        if (selectedDestination === 'serviceProviderConfig') {
            return 'Service Provider Configuration';
        }
        if (selectedDestination === 'functionCatalog' || selectedDestination === 'admin/functionCatalog') {
            return 'Function Catalog';
        }
        if (selectedDestination === 'admin/pluginfunctions' || selectedDestination === 'admin/pluginfunctions/invoke' || selectedDestination === 'admin/pluginfunctions/table') {
            return 'Plug-in Functions';
        }

        // Entity types that use singularizer and get " Management" suffix
        if (selectedDestination?.startsWith('localities/')) {
            const localityType = selectedDestination.substring(11);
            return `${getSingularLabel(localityType)} Management`;
        }
        if (selectedDestination?.startsWith('admin/')) {
            const entityPart = selectedDestination.substring(6).split('/')[0];
            return `${getSingularLabel(entityPart)} Management`;
        }
        if (selectedDestination?.toLowerCase().startsWith('other/')) {
            const entityPart = selectedDestination.replace(/^other\//i, '').split('/')[0];
            return `${getSingularLabel(entityPart)} Management`;
        }
        if (selectedDestination?.startsWith('meta/')) {
            return `${getSingularLabel(selectedDestination.split('/')[1])} Management`;
        }

        // Default: use singularizer for entity type and add " Management"
        if (selectedDestination) {
            // Convert plural route names to singular for singularizer
            const singularMap: Record<string, string> = {
                'groups': 'Group',
                'classes': 'Class',
                'resourceTypes': 'ResourceType',
                'schemaentity': 'SchemaEntity',
                'schemaattribute': 'SchemaAttribute',
                'schemabinding': 'SchemaBinding'
            };
            const lowerDest = selectedDestination.toLowerCase();
            const singularForm = singularMap[lowerDest] || selectedDestination;
            return `${getSingularLabel(singularForm)} Management`;
        }

        return '';
    };

    /** Category label for header when destination is admin/other/meta/localities/Person (null = no prefix). Used for "Category - MgmtName" when there is room. */
    const getDestinationCategory = (): string | null => {
        if (!selectedDestination || selectedDestination === 'dashboard') return null;
        if (selectedDestination === 'Person') return 'People';
        if (selectedDestination.startsWith('admin/')) return 'Admin';
        if (selectedDestination.toLowerCase().startsWith('other/')) return 'Other';
        if (selectedDestination.startsWith('meta/')) return 'Meta';
        if (selectedDestination.startsWith('localities/')) return 'Localities';
        return null;
    };

    useEffect(() => {
        if (selectedDestination === 'dashboard' || !activePageKey) return;

        const timer = setTimeout(() => {
            const label = getDestinationLabel();
            if (!label) return;
            const currentHash = window.location.hash.replace(/^#/, '') || selectedDestination;

            let category: RecentPage['category'];
            let pathAfterCategory: string[];
            if (selectedDestination === 'Person') {
                category = 'person';
                // Use hash so entity view (e.g. Person/Applicant/view/123) gives path ["Applicant","view","123"] for tooltip
                pathAfterCategory = currentHash.toLowerCase().startsWith('person/')
                    ? currentHash.replace(/^person\/?/i, '').split('/').filter(Boolean)
                    : [selectedEntityType ?? 'person'];
            } else if (selectedDestination.startsWith('localities/')) {
                category = 'localities';
                // Use hash so entity view (e.g. localities/School/view/123) gives path ["School","view","123"] for tooltip
                pathAfterCategory = (currentHash.startsWith('localities/') ? currentHash : selectedDestination)
                    .substring(11).split('/').filter(Boolean);
            } else if (selectedDestination.startsWith('admin/')) {
                category = 'admin';
                pathAfterCategory = (currentHash.startsWith('admin/') ? currentHash : selectedDestination)
                    .substring(6).split('/').filter(Boolean);
            } else if (selectedDestination.startsWith('meta/')) {
                category = 'meta';
                pathAfterCategory = (currentHash.startsWith('meta/') ? currentHash : selectedDestination)
                    .substring(5).split('/').filter(Boolean);
            } else if (selectedDestination.toLowerCase().startsWith('other/')) {
                category = 'other';
                pathAfterCategory = (currentHash.toLowerCase().startsWith('other/') ? currentHash : selectedDestination)
                    .replace(/^other\//i, '').split('/').filter(Boolean);
            } else {
                pathAfterCategory = selectedDestination.split('/').filter(Boolean);
            }
            let iconKey = pathAfterCategory[0] ?? selectedDestination;
            // Recent-page icons should use entity-type keys (singular), not route slugs.
            // Avoid passing plural meta route keys like "resourceTypes" -> "resourcetypes".
            if (typeof iconKey === 'string' && iconKey.toLowerCase() === 'resourcetypes') {
                iconKey = 'resourcetype';
            }

            setRecentPages(prev => {
                const page: RecentPage = {
                    destination: activePageKey,
                    hash: currentHash,
                    label,
                    icon: iconKey,
                    color: getEntityColor(iconKey),
                    timestamp: Date.now(),
                    category,
                    pathAfterCategory: pathAfterCategory.length ? pathAfterCategory : undefined,
                };
                const existingIdx = prev.findIndex(p =>
                    p.destination === activePageKey || p.destination === selectedDestination
                );
                if (existingIdx >= 0) {
                    // Update in place — keep its position so clicking a recent page doesn't reorder
                    const updated = [...prev];
                    updated[existingIdx] = page;
                    // Remove any other stale duplicate (different key, same destination)
                    return updated.filter((p, i) =>
                        i === existingIdx || (p.destination !== activePageKey && p.destination !== selectedDestination)
                    ).slice(0, MAX_RECENT_PAGES);
                }
                // New page — add to front
                const withoutStale = prev.filter(p =>
                    p.destination !== activePageKey && p.destination !== selectedDestination
                );
                return [page, ...withoutStale].slice(0, MAX_RECENT_PAGES);
            });
        }, 250);

        return () => clearTimeout(timer);
    }, [activePageKey, selectedEntityId]);

    // Debug logging for selectedDestination changes
    useEffect(() => {
        debugVerboseLog(`🔍 App: selectedDestination changed to: "${selectedDestination}"`);
        debugVerboseLog(`🔍 App: selectedEntityType: "${selectedEntityType}"`);
        debugVerboseLog(`🔍 App: selectedEntityId: "${selectedEntityId}"`);
    }, [selectedDestination, selectedEntityType, selectedEntityId]);

    // Initialize entity actions
    useEffect(() => {
        initializeEntityActions();
    }, []);

    // Reset scroll position when destination changes
    useEffect(() => {
        // Reset scroll position to top when destination changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [selectedDestination]);

    // Page title: use destination + entity id; display name is set by the component that has the entity (no fetch in App)
    const [entityDisplayNameForTitle, setEntityDisplayNameForTitle] = useState<string | null>(null);

    useEffect(() => {
        const destinationLabel = getDestinationLabel();
        let currentEntityId = selectedEntityId;
        const hash = window.location.hash.substring(1);
        if (hash) {
            const [hashWithoutSearch] = hash.split('?');
            const parsedEntityScreen = parseEntityScreenHash(hashWithoutSearch);
            if (parsedEntityScreen) {
                currentEntityId = parsedEntityScreen.entityId;
            } else {
                const parts = hashWithoutSearch.split('/');
                if (parts.length === 4 && (parts[2]?.toLowerCase() === 'view' || parts[2]?.toLowerCase() === 'edit')) {
                    const section = parts[0].toLowerCase();
                    const entityId = parts[3];
                    if (section === 'person' || section === 'admin' || section === 'other' || section === 'localities') currentEntityId = entityId;
                } else if (parts.length === 3) {
                    const section = parts[0].toLowerCase();
                    const entityType = parts[1];
                    const entityId = parts[2];
                    const secondLower = (entityType || '').toLowerCase();
                    const thirdLower = (entityId || '').toLowerCase();
                    if ((section === 'groups' || section === 'classes') && (secondLower === 'view' || secondLower === 'edit')) currentEntityId = entityId;
                    else if (section === 'person' || section === 'admin' || section === 'other' || section === 'localities') {
                        if (thirdLower === 'create') currentEntityId = null;
                        else currentEntityId = entityId;
                        if (section === 'admin' && entityType?.toLowerCase() === 'pluginfunctions' && (entityId?.toLowerCase() === 'invoke' || entityId?.toLowerCase() === 'table')) currentEntityId = null;
                    }
                } else if (parts.length === 2 && (parts[0].toLowerCase() === 'groups' || parts[0].toLowerCase() === 'classes') && parts[1]?.toLowerCase() !== 'create') {
                    currentEntityId = parts[1];
                }
            }
        }
        const label = entityDisplayNameForTitle || currentEntityId;
        const title = label && destinationLabel ? `Hades - ${label} - ${destinationLabel}` : label ? `Hades - ${label}` : destinationLabel ? `Hades - ${destinationLabel}` : 'Hades';
        document.title = title;
    }, [selectedDestination, selectedEntityType, selectedEntityId, entityDisplayNameForTitle]);

    // Clear deferred display name when entity id changes so we don't show a stale name
    useEffect(() => {
        setEntityDisplayNameForTitle(null);
    }, [selectedEntityId]);

    // Entity types and nav are driven by tier1 entitlements (EntityTypesContext). No schema entity/attribute load here.

    // Mark authentication as resolved when MSAL is not in progress AND user is authenticated
    useEffect(() => {
        debugVerboseLog(`🔐 App: Authentication status changed - isAuthenticated: ${isAuthenticated}, accounts.length: ${accounts.length}, inProgress: ${inProgress}, isDirectTokenAuth: ${isDirectTokenAuth}`);

        // Authentication is resolved if:
        // 1. MSAL is not in progress AND (user has MSAL accounts OR using direct token)
        // 2. OR using direct token (bypasses MSAL)
        if ((inProgress === 'none' && isAuthenticated) || isDirectTokenAuth) {
            debugVerboseLog(`🔐 App: Authentication resolved - setting authResolved to true`);
            setAuthResolved(true);
        } else if (inProgress === 'none' && !isAuthenticated && !isDirectTokenAuth) {
            debugVerboseLog(`🔐 App: Authentication resolved but user not authenticated - setting authResolved to false`);
            setAuthResolved(false);
        }
    }, [isAuthenticated, accounts.length, inProgress, isDirectTokenAuth]);

    // Parse hash from URL on component mount and when hash changes (Phase 3: delegated to routes/hashRouter)
    useEffect(() => {
        const hash = typeof window !== 'undefined' ? window.location.hash : '';
        const hashIndicatesEdit = /\/edit\//i.test(hash);
        if (inProgress !== 'none' && !hashIndicatesEdit) {
            debugVerboseLog(`🔐 App: MSAL still in progress (${inProgress}), skipping hash parsing`);
            return;
        }

        const handleHash = () => {
            const rawHash = window.location.hash;

            // Sync parse
            const syncResult = parseHashRoute(rawHash);

            // Handle OAuth/MSAL callback normalization
            if (/\b(code|id_token|access_token|client_info|session_state|state)=/.test(rawHash)) {
                if (isAuthenticated) {
                    debugVerboseLog('🔐 App: Consuming OAuth callback hash and normalizing to #dashboard');
                    setRoute(DASHBOARD_ROUTE);
                    if (window.location.hash !== '#dashboard') {
                        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#dashboard`);
                    }
                }
                return;
            }

            if (syncResult.redirect) {
                window.location.hash = syncResult.redirect;
                return;
            }

            setRoute(syncResult.route);
            setNavOpen(true);

            // For 2-segment routes that need async categorization, do a follow-up
            const hashStr = rawHash.replace(/^#/, '');
            const [pathOnly] = hashStr.split('?');
            const segments = pathOnly.split('/').filter(Boolean);
            if (segments.length === 2) {
                parseHashAsync(rawHash).then(asyncResult => {
                    if (asyncResult.redirect) {
                        window.location.hash = asyncResult.redirect;
                    } else {
                        setRoute(asyncResult.route);
                    }
                });
            }

        };

        handleHash();

        let removeHashListener: (() => void) | null = null;
        const timer = setTimeout(() => {
            handleHash();
            const onHashChange = () => { handleHash(); };
            window.addEventListener('hashchange', onHashChange);
            removeHashListener = () => window.removeEventListener('hashchange', onHashChange);
        }, 100);

        return () => {
            clearTimeout(timer);
            removeHashListener?.();
        };
    }, [inProgress, isAuthenticated]);


    // Helper: writes the canonical hash for a destination + category; hashchange listener updates route state.
    const handleCanonicalDestination = (
        destination: string,
        _isEntity: boolean,
        category: NavigationCategory
    ) => {
        debugVerboseLog(`🔍 App: Processing ${destination} as canonical destination, category: ${category}`);

        if (category === 'utilities') {
            window.location.hash = `utilities/${destination.toLowerCase()}`;
        } else if (category === 'locality' || destination.startsWith('localities/')) {
            const localityType = destination.startsWith('localities/')
                ? destination.substring(11)
                : destination;
            window.location.hash = `localities/${localityType.toLowerCase()}`;
        } else if (category === 'meta' && !destination.startsWith('meta/')) {
            window.location.hash = `meta/${destination}`;
        } else {
            window.location.hash = destination;
        }
    };

    const navigateToHash = (hash: string) => {
        window.location.hash = hash.replace(/^#/, '');
    };

    const requestHashNavigation = (hash: string) => {
        const normalizedHash = hash.replace(/^#/, '');
        if (inlineEditorUnsavedState.active && inlineEditorUnsavedState.hasUnsavedChanges) {
            setPendingNavigation({ kind: 'hash', hash: normalizedHash });
            setUnsavedNavigationDialogOpen(true);
            return;
        }
        navigateToHash(normalizedHash);
    };

    const performDestinationNavigation = async (
        destination: string,
        isEntity: boolean = true,
        category?: NavigationCategory
    ) => {
        window.scrollTo({ top: 0, behavior: 'smooth' });

        const knownMetaDestinations = ['schemaentity', 'schemaattribute', 'schemabinding', 'meta/schemaentity', 'meta/schemaattribute', 'meta/schemabinding', 'resourceTypes', 'meta/resourceTypes', 'serviceProviderConfig', 'meta/serviceProviderConfig', 'testing', 'jsTesting', 'customFilter', 'patchTesting', 'scimSelect', 'speedComparison', 'searchCompare', 'encdec', 'setGen', 'contentTypes', 'searchIndexes', 'userSettings', 'freeEdit', 'schoolMap'];

        if (destination === 'Person') {
            window.location.hash = 'Person';
        } else if (destination === 'pluginfunctions' && category === 'admin') {
            window.location.hash = 'admin/pluginfunctions/invoke';
        } else if (!knownMetaDestinations.includes(destination)) {
            let resolvedCategory = category;
            const categorizationService = EntityCategorizationService.getInstance();

            if (!resolvedCategory) {
                const categoryResult = await categorizationService.getEntityCategory(destination);
                resolvedCategory = categoryResult.type;
            }

            const relativePath = await categorizationService.getRelativePath(destination);

            if (destination === 'Person') {
                window.location.hash = 'Person';
            } else if (resolvedCategory === 'other' && String(destination).toLowerCase() === 'policyruleaudit') {
                window.location.hash = 'utilities/policyruleaudit';
            } else {
                window.location.hash = relativePath;
            }
        } else {
            let resolvedCategory: NavigationCategory;
            if (['testing', 'jsTesting', 'customFilter', 'patchTesting', 'scimSelect', 'speedComparison', 'searchCompare', 'encdec', 'setGen', 'policyRuleAudit', 'contentTypes', 'searchIndexes', 'userSettings', 'freeEdit'].includes(destination)) {
                resolvedCategory = 'utilities';
            } else {
                resolvedCategory = category || 'other';
            }
            handleCanonicalDestination(destination, isEntity, resolvedCategory);
        }
    };

    const handleNavigation = async (
        destination: string,
        isEntity: boolean = true,
        category?: NavigationCategory
    ) => {
        if (inlineEditorUnsavedState.active && inlineEditorUnsavedState.hasUnsavedChanges) {
            setPendingNavigation({ kind: 'destination', destination, isEntity, category });
            setUnsavedNavigationDialogOpen(true);
            return;
        }

        await performDestinationNavigation(destination, isEntity, category);
    };

    const handleConfirmPendingNavigation = () => {
        const nextNavigation = pendingNavigation;
        setUnsavedNavigationDialogOpen(false);
        setPendingNavigation(null);
        setInlineEditorUnsavedState(EMPTY_INLINE_EDITOR_UNSAVED_STATE);

        if (!nextNavigation) return;

        if (nextNavigation.kind === 'hash') {
            navigateToHash(nextNavigation.hash);
            return;
        }

        void performDestinationNavigation(
            nextNavigation.destination,
            nextNavigation.isEntity,
            nextNavigation.category
        );
    };

    const handleImpersonationChange = useCallback(() => {
        setRoute(DASHBOARD_ROUTE);
    }, []);

    const renderUtilityPage = (page: string) => {
        switch (page) {
            case 'testing': return <ScimApiTesting />;
            case 'jsTesting': return <JsTesting />;
            case 'customFilter': return <ScimFilterConverterUI fieldOptions={fieldOptions} />;
            case 'patchTesting': return <PatchTesting authResolved={authResolved} />;
            case 'scimSelect': return <ScimSelect authResolved={authResolved} />;
            case 'speedComparison': return <SpeedComparison />;
            case 'searchCompare': return <SearchCompare />;
            case 'encdec': return <EncDec />;
            case 'setGen': return <SetGen />;
            case 'policyRuleAudit': return <PolicyRuleAudit />;
            case 'contentTypes': return <ContentTypesTesting />;
            case 'searchIndexes': return <SearchIndexes />;
            case 'userSettings': return <UserSettingsTable />;
            case 'freeEdit': return <FreeEdit />;
            default: return null;
        }
    };

    const renderSpecialPage = (page: string) => {
        switch (page) {
            case 'schoolMap': return <SchoolMapView authResolved={authResolved} />;
            case 'serviceProviderConfig': return <ServiceProviderConfigManagement authResolved={authResolved} />;
            case 'resourceTypes': return (
                <Box p="md">
                    <Alert color="blue">
                        Resource Types section is no longer available. Entity types are driven by schema and entitlements.
                    </Alert>
                    <Button mt="md" variant="outline" onClick={() => { window.location.hash = 'meta/schemabinding'; }}>
                        Go to Schema Bindings
                    </Button>
                </Box>
            );
            default: return null;
        }
    };

    const renderEntityRoute = () => {
        if (route.type !== 'entity') return null;
        const { section, entityType } = route;
        const lowerType = entityType.toLowerCase();

        if (section === 'localities') {
            let localityType: string;
            if (lowerType === 'responsibilitycenter') localityType = 'ResponsibilityCenter';
            else if (lowerType === 'studentbusstop') localityType = 'StudentBusStop';
            else localityType = entityType.charAt(0).toUpperCase() + entityType.slice(1);
            return (
                <LocalityManagement
                    selectedLocalityType={localityType as 'ResponsibilityCenter' | 'Class' | 'Building' | 'School'}
                    isAuthenticated={isAuthenticated}
                    selectedEntityId={selectedEntityId}
                    selectedEntityViewMode={selectedEntityViewMode}
                    deepLinkEntityType={selectedEntityType}
                    authResolved={authResolved}
                    onBehalfOfKey={onBehalfOfKey}
                    onUnsavedStateChange={handleInlineEditorUnsavedStateChange}
                />
            );
        }

        if (section === 'admin' && lowerType === 'functioncatalog') {
            return <FunctionCatalogManagement authResolved={authResolved} selectedEntityId={selectedEntityId} />;
        }

        if ((section === 'other' || section === 'meta') && lowerType === 'schemabinding') {
            return <SchemaBindingManagement selectedEntityId={selectedEntityId} deepLinkEntityType={selectedEntityType} authResolved={authResolved} />;
        }

        if (section === 'person') {
            return (
                <PersonManagement
                    selectedEntityType={selectedEntityType}
                    selectedEntityId={selectedEntityId}
                    selectedEntityViewMode={selectedEntityViewMode}
                    deepLinkEntityType={selectedEntityType}
                    authResolved={authResolved}
                    onBehalfOfKey={onBehalfOfKey}
                    onEntityDisplayNameForTitle={setEntityDisplayNameForTitle}
                    onUnsavedStateChange={handleInlineEditorUnsavedStateChange}
                />
            );
        }

        return (
            <DynamicEntityManagement
                entityType={entityType}
                selectedEntityId={selectedEntityId}
                selectedEntityViewMode={selectedEntityViewMode}
                deepLinkEntityType={selectedEntityType}
                authResolved={authResolved}
                onBehalfOfKey={onBehalfOfKey}
                listViewHashBase={`${section}/${entityType}`}
                onUnsavedStateChange={handleInlineEditorUnsavedStateChange}
            />
        );
    };

    const classColumns: Array<TableColumn<Class>> = [
        {
            key: 'DisplayName', label: 'Name', title: 'DisplayName', width: '40%', sortable: true,
            render: (classItem: any, index: number) => (
                <DisplayNameCell displayName={classItem.DisplayName || classItem.rawData?.DisplayName} id={classItem.Id} index={index} startIndex={0} />
            )
        },
        { key: 'Type', label: 'Course', title: 'Course', width: '30%', sortable: true },
        { key: 'Id', label: 'Id', title: 'Id', width: '30%', sortable: true }
    ];

    const renderRouteContent = (): React.ReactNode => {
        switch (route.type) {
            case 'dashboard':
                return null;
            case 'groups':
                return (
                    <GroupsManagement
                        selectedEntityId={selectedEntityId}
                        selectedEntityViewMode={selectedEntityViewMode}
                        deepLinkEntityType={selectedEntityType ?? null}
                        authResolved={authResolved}
                        onBehalfOfKey={onBehalfOfKey}
                        onUnsavedStateChange={handleInlineEditorUnsavedStateChange}
                    />
                );
            case 'classes':
                return (
                    <LocalityEntityManagement<Class>
                        entityType="Class"
                        hashBase="#classes"
                        selectedEntityId={selectedEntityId}
                        selectedEntityViewMode={selectedEntityViewMode}
                        deepLinkEntityType={selectedEntityType ?? null}
                        authResolved={authResolved}
                        onBehalfOfKey={onBehalfOfKey}
                        disableRowSelectWhenEditing={true}
                        search={(p) => scimClassService.searchClasses(p)}
                        fetchById={(id) => entityService.getClassById(id)}
                        generateColumns={() => classColumns}
                        onUnsavedStateChange={handleInlineEditorUnsavedStateChange}
                    />
                );
            case 'plugin':
                return (
                    <PluginFunctionsManagement
                        tabFromRoute={route.sub === 'table' ? 1 : 0}
                        onTabChange={(tab) => { window.location.hash = tab === 1 ? 'admin/pluginfunctions/table' : 'admin/pluginfunctions/invoke'; }}
                        pluginFunctionIdFromRoute={selectedPluginFunctionId}
                        pivotEntityIdFromRoute={selectedPivotEntityId}
                        pivotDetailIdFromRoute={selectedPivotDetailId}
                        prepareCatalogIdFromRoute={selectedPrepareCatalogId}
                        prepareDetailIdFromRoute={selectedPrepareDetailId}
                        authResolved={authResolved}
                        onBehalfOfKey={onBehalfOfKey}
                        selectedEntityId={selectedEntityId}
                        selectedEntityViewMode={selectedEntityViewMode}
                        deepLinkEntityType={selectedEntityType}
                    />
                );
            case 'utility':
                return renderUtilityPage(route.page);
            case 'special':
                return renderSpecialPage(route.page);
            case 'entity':
                return renderEntityRoute();
        }
    };

    const isDashboard = route.type === 'dashboard';

    // Dashboard: Escape navigates back (e.g. return to the table you came from), unless a dialog/overlay handled Escape.
    // Chris requested this behavior.
    useEffect(() => {
        if (!isAuthenticated || !isDashboard) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (e.defaultPrevented) return;
            const t = e.target;
            if (t instanceof Element && t.closest('[role="dialog"]')) return;
            e.preventDefault();
            window.history.back();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isAuthenticated, isDashboard]);

    return (
        <>
            {!isAuthenticated ? (
                <LandingPage />
            ) : (
                <EntityTypesProvider>
                    <AuthRefreshHandler />
                    <AppShell
                        isDashboard={isDashboard}
                        navRailOpen={navRailOpen}
                        onToggleNavRail={() => setNavRailOpen((o) => !o)}
                        pageLabel={getDestinationLabel()}
                        categoryLabel={getDestinationCategory()}
                        requestApprovalSummary={requestApprovalSummary}
                        onNavigate={handleNavigation}
                        onNavigateHash={requestHashNavigation}
                        onImpersonationChange={handleImpersonationChange}
                        selectedDestination={selectedDestination}
                        activePageKey={activePageKey}
                        authResolved={authResolved}
                        recentPages={recentPages}
                        onDismissRecentPage={dismissRecentPage}
                        dashboardContent={
                            <Dashboard
                                onNavigate={handleNavigation}
                                onNavigateHash={requestHashNavigation}
                                authResolved={authResolved}
                                active={isDashboard}
                            />
                        }
                        routeContent={renderRouteContent()}
                        footerContent={
                            <Footer navOpen={false} onImpersonationChange={handleImpersonationChange} />
                        }
                        dialogsContent={
                            <ShadcnUnsavedChangesDialog
                                open={unsavedNavigationDialogOpen}
                                onClose={() => {
                                    setUnsavedNavigationDialogOpen(false);
                                    setPendingNavigation(null);
                                }}
                                onConfirm={handleConfirmPendingNavigation}
                                mode={inlineEditorUnsavedState.mode}
                                entityType={inlineEditorUnsavedState.entityType}
                            />
                        }
                    />
                </EntityTypesProvider>
            )}
        </>
    );
}

function App() {
    return (
        <QueryClientProvider client={queryClient}>
        <Provider store={store}>
            <MantineProvider theme={mantineTheme} defaultColorScheme="dark">
                <ModalsProvider>
                    <Notifications position="top-right" />
                    <ErrorBoundary>
                        <AuthProvider>
                            <ThemeProvider>
                                <ViewModeProvider>
                                    <AppContent />
                                </ViewModeProvider>
                            </ThemeProvider>
                        </AuthProvider>
                    </ErrorBoundary>
                </ModalsProvider>
            </MantineProvider>
        </Provider>
        </QueryClientProvider>
    );
}

export default App; 